
import React, { useState } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Search, FileText, Pencil, Badge, BadgePercent, Calendar } from "lucide-react";
import { 
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

// Dummy promotion data
const dummyPromotions = [
  {
    id: 1,
    title: "Summer Sale",
    type: "% Discount",
    channel: "All",
    targetSegment: "All",
    startDate: "2025-06-01",
    endDate: "2025-06-30",
    status: "Active",
    redemptions: 145,
    views: 1250,
  },
  {
    id: 2,
    title: "Buy 1 Get 1 Free",
    type: "Buy X Get Y",
    channel: "POS",
    targetSegment: "ANGKASA Members",
    startDate: "2025-05-15",
    endDate: "2025-05-31",
    status: "Active",
    redemptions: 87,
    views: 520,
  },
  {
    id: 3,
    title: "Holiday Bundle",
    type: "Bundle Deal",
    channel: "JimatShop",
    targetSegment: "All",
    startDate: "2025-07-01",
    endDate: "2025-07-15",
    status: "Draft",
    redemptions: 0,
    views: 0,
  },
];

const AllPromotionsTab: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");

  const filteredPromotions = dummyPromotions.filter(promotion => {
    const matchesSearch = promotion.title.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === "all" || promotion.status === statusFilter;
    const matchesType = typeFilter === "all" || promotion.type === typeFilter;
    return matchesSearch && matchesStatus && matchesType;
  });

  const getStatusBadge = (status: string) => {
    if (status === "Active") {
      return "bg-green-100 text-green-800";
    } else if (status === "Draft") {
      return "bg-gray-100 text-gray-800";
    } else if (status === "Expired") {
      return "bg-red-100 text-red-800";
    }
    return "bg-blue-100 text-blue-800";
  };

  const getIconByType = (type: string) => {
    switch (type) {
      case "% Discount":
        return <BadgePercent className="h-4 w-4 mr-1" />;
      case "Buy X Get Y":
        return <Badge className="h-4 w-4 mr-1" />;
      case "Bundle Deal":
        return <Badge className="h-4 w-4 mr-1" />;
      case "Voucher Code":
        return <FileText className="h-4 w-4 mr-1" />;
      default:
        return <Badge className="h-4 w-4 mr-1" />;
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-col md:flex-row gap-4 justify-between">
        <div className="relative flex-1">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search promotions..."
            className="pl-8"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <div className="flex gap-2">
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[150px]">
              <SelectValue placeholder="All Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="Active">Active</SelectItem>
              <SelectItem value="Draft">Draft</SelectItem>
              <SelectItem value="Expired">Expired</SelectItem>
            </SelectContent>
          </Select>
          <Select value={typeFilter} onValueChange={setTypeFilter}>
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="All Types" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Types</SelectItem>
              <SelectItem value="% Discount">% Discount</SelectItem>
              <SelectItem value="Buy X Get Y">Buy X Get Y</SelectItem>
              <SelectItem value="Bundle Deal">Bundle Deal</SelectItem>
              <SelectItem value="Voucher Code">Voucher Code</SelectItem>
            </SelectContent>
          </Select>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button variant="outline" className="flex items-center gap-2">
                  <FileText className="h-4 w-4" />
                  Export
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p>Export promotions data</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      </div>

      <div className="border rounded-md overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Title</TableHead>
              <TableHead>Type</TableHead>
              <TableHead>Channel</TableHead>
              <TableHead>Target Segment</TableHead>
              <TableHead>Period</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Redemptions</TableHead>
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredPromotions.length > 0 ? (
              filteredPromotions.map((promotion) => (
                <TableRow key={promotion.id}>
                  <TableCell className="font-medium">{promotion.title}</TableCell>
                  <TableCell>
                    <div className="flex items-center">
                      {getIconByType(promotion.type)}
                      {promotion.type}
                    </div>
                  </TableCell>
                  <TableCell>{promotion.channel}</TableCell>
                  <TableCell>{promotion.targetSegment}</TableCell>
                  <TableCell>
                    <div className="flex items-center">
                      <Calendar className="h-4 w-4 mr-1" />
                      <span>{new Date(promotion.startDate).toLocaleDateString()} - {new Date(promotion.endDate).toLocaleDateString()}</span>
                    </div>
                  </TableCell>
                  <TableCell>
                    <span className={`px-2 py-1 rounded-md text-xs font-medium ${getStatusBadge(promotion.status)}`}>
                      {promotion.status}
                    </span>
                  </TableCell>
                  <TableCell>{promotion.redemptions}</TableCell>
                  <TableCell>
                    <div className="flex gap-2">
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button size="icon" variant="ghost">
                              <Pencil className="h-4 w-4" />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>
                            <p>Edit Promotion</p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={8} className="text-center py-4 text-muted-foreground">
                  No promotions found
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};

export default AllPromotionsTab;
